import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, User, BookOpen } from 'lucide-react';
import { ProofSection } from '../components/ProofSection';
import { Navigation } from '../components/Navigation';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const blogPosts = [{
  title: 'Tips for Building a Kid-Friendly Martial Arts Studio at Home',
  date: 'May 13th, 2020',
  author: 'Master Joe',
  image: 'https://images.unsplash.com/photo-1555597673-b21d5c935865?w=800&h=500&fit=crop',
  excerpt: 'If the coronavirus lockdown is preventing your kids from practicing martial arts at their local studio, find a way to help them continue their training at home...',
  content: `
      <h2>The Benefits of Martial Arts for Kids</h2>
      <p>Setting up a home dojo might seem like a lot of work, but being able to practice martial arts at home will benefit your children in several ways.</p>
      <ul>
        <li>Kids need at least an hour of moderate-to-vigorous exercise every single day. Martial arts training is an excellent way to help your kids meet these activity guidelines, especially if you can't go outside.</li>
        <li>Learning new skills and progressing through martial arts training is a great way to build confidence and teach kids about perseverance.</li>
        <li>Although the martial arts involve fighting and combat techniques, the practice actually promotes peaceful conflict resolution over violence.</li>
      </ul>

      <h2>Making Space in Your Home for a Studio</h2>
      <p>Depending on the type of martial arts your kids will be practicing at home, you might need quite a lot of space. Basements work really well for this purpose!</p>
      <ul>
        <li>Find out how much it will cost to remodel your basement into a home gym area or martial arts studio.</li>
        <li>Declutter and clean out your basement so your kids have room to practice safely.</li>
        <li>Prevent your martial arts studio from hurting your home value by designing it as a multi-purpose room that future buyers could use for something else.</li>
      </ul>

      <h2>At-Home Martial Arts Gear for Kids</h2>
      <p>While kids don't need much equipment to practice martial arts at home, a few pieces of gear can help them get the most out of their training sessions.</p>
      <ul>
        <li>Protective gear like helmets and mouthguards will keep your kids safe while they're sparring.</li>
        <li>Boxing gloves are an important element of learning gear that will keep your kids safe when they're throwing punches at a dummy or heavy bag.</li>
        <li>If you want to get involved in your kids' training sessions, purchase some foam blockers and targets so they can practice their timing and accuracy.</li>
      </ul>

      <p>Don't let your kids miss out on the benefits of martial arts training during the coronavirus lockdown. It's easy to set up a studio in your own home, as long as you have the space for it. With a couple pieces of safety gear and training equipment, you'll be ready to start training through drills with your kids—you might even learn a few techniques yourself!</p>
    `
}];
export function BlogPage() {
  const post = blogPosts[0];
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-180">
      <Navigation data-id="element-181" />

      {/* Hero Section */}
      <section className="pt-16 pb-12 px-4 bg-gradient-to-b from-blue-50 to-white" data-id="element-182">
        <div className="max-w-4xl mx-auto text-center" data-id="element-183">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-6" data-id="element-184">
            <div className="flex items-center justify-center gap-3 mb-4" data-id="element-185">
              <BookOpen className="w-8 h-8 text-[#1e3a8a]" data-id="element-186" />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-[#1e3a8a]" data-id="element-187">
              Blog
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="element-188">
              Tips, insights, and stories from the NWBBA community
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Post */}
      <article className="py-16 px-4" data-id="element-189">
        <div className="max-w-4xl mx-auto" data-id="element-190">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-191">
            {/* Featured Image */}
            <div className="relative aspect-video bg-gray-200 rounded-2xl overflow-hidden shadow-xl mb-8" data-id="element-192">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" data-id="element-193" />
            </div>

            {/* Post Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6" data-id="element-194">
              <div className="flex items-center gap-2" data-id="element-195">
                <Calendar className="w-4 h-4" data-id="element-196" />
                <span data-id="element-197">{post.date}</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-198">
                <User className="w-4 h-4" data-id="element-199" />
                <span data-id="element-200">{post.author}</span>
              </div>
            </div>

            {/* Post Title */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#1e3a8a] mb-8 leading-tight" data-id="element-201">
              {post.title}
            </h1>

            {/* Post Content */}
            <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{
            __html: post.content
          }} style={{
            lineHeight: '1.8'
          }} data-id="element-202" />

            <style data-id="element-203">{`
              .prose h2 {
                font-size: 1.75rem;
                font-weight: 800;
                color: #1e3a8a;
                margin-top: 2.5rem;
                margin-bottom: 1.25rem;
              }
              .prose p {
                color: #4b5563;
                margin-bottom: 1.5rem;
                line-height: 1.8;
              }
              .prose ul {
                list-style: none;
                padding-left: 0;
                margin-bottom: 2rem;
              }
              .prose ul li {
                position: relative;
                padding-left: 2rem;
                margin-bottom: 1rem;
                color: #4b5563;
                line-height: 1.8;
              }
              .prose ul li:before {
                content: "•";
                position: absolute;
                left: 0.5rem;
                color: #dc2626;
                font-weight: bold;
                font-size: 1.5rem;
              }
            `}</style>
          </motion.div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-204">
        <div className="max-w-4xl mx-auto text-center" data-id="element-205">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-206">
            <h2 className="text-3xl sm:text-4xl font-black text-[#1e3a8a] mb-6" data-id="element-207">
              Ready to Start Your Journey?
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed max-w-2xl mx-auto" data-id="element-208">
              Join our community and experience the life-changing benefits of
              martial arts training.
            </p>
            <Link to="/" className="inline-block px-8 py-4 bg-[#dc2626] text-white text-lg font-bold rounded-lg shadow-lg hover:bg-red-700 transition-colors" data-id="element-209">
              Start Your $29 Trial
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Proof Section */}
      <ProofSection testimonialIndex={5} data-id="element-210" />

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center text-sm" data-id="element-211">
        <div className="max-w-4xl mx-auto" data-id="element-212">
          <div className="font-black text-xl text-white mb-4 tracking-tighter" data-id="element-213">
            NWBBA
          </div>
          <p className="mb-6" data-id="element-214">Northwest Black Belt Academy</p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-8" data-id="element-215">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-216">
              Home
            </Link>
            <Link to="/about" className="hover:text-white transition-colors" data-id="element-217">
              About
            </Link>
            <Link to="/classes" className="hover:text-white transition-colors" data-id="element-218">
              Classes
            </Link>
            <Link to="/schedule" className="hover:text-white transition-colors" data-id="element-219">
              Schedule
            </Link>
            <Link to="/resources" className="hover:text-white transition-colors" data-id="element-220">
              Resources
            </Link>
            <Link to="/blog" className="text-white font-bold" data-id="element-221">
              Blog
            </Link>
            <Link to="/proof" className="hover:text-white transition-colors" data-id="element-222">
              Proof
            </Link>
            <a href="#" className="hover:text-white transition-colors" data-id="element-223">
              Contact
            </a>
          </div>
          <p data-id="element-224">
            &copy; {new Date().getFullYear()} Northwest Black Belt Academy. All
            rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}